import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { PolizaCreditoService } from '../../../../../services/finanzas/poliza-credito/poliza-credito.service';
import { Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import { CreditPolicyOperationDialogComponent } from '../credit-policy-operation-dialog/credit-policy-operation-dialog.component';
import {
  CREDIT_POLICY_BANK_MOVEMENT,
  CREDIT_POLICY_CASH_MOVEMENT,
  CreditPolicyOperationKind,
  CreditPolicyRow,
  creditPolicyUsedAmount,
} from '../../models/credit-policy-operation.model';

interface CreditPolicyMovementLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
}

interface CreditPolicyMovementBlock {
  id: string;
  kind: CreditPolicyOperationKind;
  movementType?: number;
  title: string;
  icon: string;
  singular: string;
  deletedText: string;
  conceptLabel: string;
  amountLabel: string;
  emptyText: string;
  total: number;
  count: number;
  lines: CreditPolicyMovementLine[];
}

const MAX_LINES = 5;

@Component({
  selector: 'app-credit-policy-detail',
  templateUrl: './credit-policy-detail.component.html',
  styleUrls: ['./credit-policy-detail.component.scss'],
  standalone: false,
})
export class CreditPolicyDetailComponent implements OnChanges {
  @Input() row!: CreditPolicyRow;
  @Output() changed = new EventEmitter<void>();

  readonly maxLines = MAX_LINES;

  loading = true;
  error = false;
  blocks: CreditPolicyMovementBlock[] = [];

  private loadedUid = '';

  constructor(
    private polizaCreditoService: PolizaCreditoService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get limit(): number {
    return Number(this.row && this.row.limite) || 0;
  }

  get used(): number {
    return creditPolicyUsedAmount(this.row && this.row.dispuesto);
  }

  get percent(): number {
    if (this.limit <= 0) return 0;
    return Math.min(100, Math.max(0, (this.used / this.limit) * 100));
  }

  get overLimit(): boolean {
    return this.limit > 0 && this.used > this.limit;
  }

  ngOnChanges(): void {
    if (!this.row || !this.row.uid) return;
    if (this.row.uid === this.loadedUid) return;
    this.loadedUid = this.row.uid;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(block: CreditPolicyMovementBlock): void {
    this.openDialog(block);
  }

  manage(block: CreditPolicyMovementBlock): void {
    this.openDialog(block);
  }

  editLine(block: CreditPolicyMovementBlock, line: CreditPolicyMovementLine): void {
    this.openDialog(block, line.uid);
  }

  deleteLine(block: CreditPolicyMovementBlock, line: CreditPolicyMovementLine): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: `Eliminar ${block.singular}`,
      subtitle: [this.row.banco, this.row.iban].filter(v => !!v).join(' · ') || undefined,
      message: 'Se eliminará el registro y su asiento contable. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(block, line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando…',
      errorText: 'No se pudo eliminar el registro',
      action: () => this.deleteRequest(block, line.uid).then(ok => { if (ok === false) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok(block.deletedText);
      this.changed.emit();
    });
  }

  trackByUid(_: number, line: CreditPolicyMovementLine): string {
    return line.uid;
  }

  trackByBlock(_: number, block: CreditPolicyMovementBlock): string {
    return block.id;
  }

  private openDialog(block: CreditPolicyMovementBlock, editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    CreditPolicyOperationDialogComponent.open(this.dialog, {
      policy: this.row,
      kind: block.kind,
      movementType: block.movementType,
      editUid,
    }).subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private deleteRequest(block: CreditPolicyMovementBlock, uid: string): Promise<boolean> {
    if (block.kind === 'constitution') return this.polizaCreditoService.constitucionDelete(uid);
    if (block.kind === 'settlement') return this.polizaCreditoService.liquidacionesDelete(uid);
    if (block.kind === 'bank') return this.polizaCreditoService.movimientoBancoPolizaCreditoDelete(uid);
    return this.polizaCreditoService.movimientoEfectivoPolizaCreditoDelete(uid);
  }

  private load(uid: string): void {
    this.loading = true;
    this.error = false;
    const safe = (promise: Promise<any[]>) => promise.then(list => Array.isArray(list) ? list : []);

    Promise.all([
      safe(this.polizaCreditoService.constitucionList(uid)),
      safe(this.polizaCreditoService.liquidacionesList(uid)),
      safe(this.polizaCreditoService.movimientoBancoPolizaCreditoList(uid, CREDIT_POLICY_BANK_MOVEMENT.traspaso)),
      safe(this.polizaCreditoService.movimientoBancoPolizaCreditoList(uid, CREDIT_POLICY_BANK_MOVEMENT.transferencia)),
      safe(this.polizaCreditoService.movimientoEfectivoPolizaCreditoList(uid, CREDIT_POLICY_CASH_MOVEMENT.reintegro)),
      safe(this.polizaCreditoService.movimientoEfectivoPolizaCreditoList(uid, CREDIT_POLICY_CASH_MOVEMENT.ingreso)),
    ])
      .then(([constitutions, settlements, transfersBetween, transfers, withdrawals, deposits]) => {
        if (!this.row || uid !== this.row.uid) return;
        this.blocks = [
          this.buildBlock({
            id: 'constitution', kind: 'constitution', title: 'Constitución', icon: 'account_balance',
            singular: 'constitución', deletedText: 'Constitución eliminada correctamente',
            conceptLabel: 'Descripción', amountLabel: 'Comisión de apertura', emptyText: 'Sin constitución registrada',
          }, this.constitutionLines(constitutions)),
          this.buildBlock({
            id: 'settlement', kind: 'settlement', title: 'Liquidaciones', icon: 'receipt',
            singular: 'liquidación', deletedText: 'Liquidación eliminada correctamente',
            conceptLabel: 'Concepto', amountLabel: 'Importe', emptyText: 'Sin liquidaciones en el ejercicio',
          }, this.movementLines(settlements, 'settlement')),
          this.buildBlock({
            id: 'bank-1', kind: 'bank', movementType: CREDIT_POLICY_BANK_MOVEMENT.traspaso, title: 'Traspasos', icon: 'swap_horiz',
            singular: 'traspaso', deletedText: 'Traspaso eliminado correctamente',
            conceptLabel: 'Concepto', amountLabel: 'Importe', emptyText: 'Sin traspasos en el ejercicio',
          }, this.movementLines(transfersBetween, 'bank')),
          this.buildBlock({
            id: 'bank-2', kind: 'bank', movementType: CREDIT_POLICY_BANK_MOVEMENT.transferencia, title: 'Transferencias', icon: 'send',
            singular: 'transferencia', deletedText: 'Transferencia eliminada correctamente',
            conceptLabel: 'Concepto', amountLabel: 'Importe', emptyText: 'Sin transferencias en el ejercicio',
          }, this.movementLines(transfers, 'bank')),
          this.buildBlock({
            id: 'cash-1', kind: 'cash', movementType: CREDIT_POLICY_CASH_MOVEMENT.reintegro, title: 'Reintegros de efectivo', icon: 'local_atm',
            singular: 'reintegro', deletedText: 'Reintegro eliminado correctamente',
            conceptLabel: 'Concepto', amountLabel: 'Importe', emptyText: 'Sin reintegros en el ejercicio',
          }, this.movementLines(withdrawals, 'cash')),
          this.buildBlock({
            id: 'cash-2', kind: 'cash', movementType: CREDIT_POLICY_CASH_MOVEMENT.ingreso, title: 'Ingresos de efectivo', icon: 'account_balance_wallet',
            singular: 'ingreso', deletedText: 'Ingreso eliminado correctamente',
            conceptLabel: 'Concepto', amountLabel: 'Importe', emptyText: 'Sin ingresos en el ejercicio',
          }, this.movementLines(deposits, 'cash')),
        ];
      })
      .catch(() => { this.error = true; })
      .then(() => { this.loading = false; });
  }

  private constitutionLines(list: any[]): CreditPolicyMovementLine[] {
    return this.sortByDate(list).map(item => ({
      uid: item.uid,
      date: item.fecha,
      concept: item.descripcion || 'Sin descripción',
      meta: item.activitiesName || '',
      amount: Number(item.comisionApertura) || 0,
    }));
  }

  private movementLines(list: any[], kind: CreditPolicyOperationKind): CreditPolicyMovementLine[] {
    return this.sortByDate(list).map(item => {
      const parts: string[] = [];
      if (kind === 'bank' && item.bankAccountInfo && item.bankAccountInfo !== '0') parts.push(item.bankAccountInfo);
      if (kind === 'cash' && item.efectivoName) parts.push(item.efectivoName);
      if (item.activitiesName) parts.push(item.activitiesName);
      if (Number(item.comisiones)) parts.push(`Comisiones ${Utils.FormatMoney(item.comisiones)} €`);
      return {
        uid: item.uid,
        date: item.fecha,
        concept: item.concepto || 'Sin concepto',
        meta: parts.join(' · '),
        amount: Number(item.importe) || 0,
      };
    });
  }

  private buildBlock(
    info: Omit<CreditPolicyMovementBlock, 'total' | 'count' | 'lines'>,
    lines: CreditPolicyMovementLine[],
  ): CreditPolicyMovementBlock {
    const total = Utils.RoundTwoDecimals(lines.reduce((acc, line) => acc + line.amount, 0));
    return { ...info, total, count: lines.length, lines: lines.slice(0, MAX_LINES) };
  }

  private details(block: CreditPolicyMovementBlock, line: CreditPolicyMovementLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: block.conceptLabel, value: line.concept });
    if (line.meta) details.push({ label: 'Detalle', value: line.meta });
    details.push({ label: block.amountLabel, value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }

  private sortByDate(list: any[]): any[] {
    return list.slice().sort((a, b) => this.time(b.fecha) - this.time(a.fecha));
  }

  private time(value: any): number {
    const time = value ? new Date(value).getTime() : 0;
    return isNaN(time) ? 0 : time;
  }
}
